import React from "react";

import {
  DecisionCriteriaDisplayValues,
  humanizeLoanTypeName,
  DecisionCriteriaMismatch,
  SandboxResponseProgram,
} from "@eave/shared/types/api";

import { dollarsTo2Places } from "../helpers";
import ReworkForm from "../ReworkForm";

const styles = require("./styles.module.scss");

const percentToNPlaces = (p: number, n: number): string => (p === 0 ? "0%" : `${p.toFixed(n)}%`);
const percentTo2Places = (p: number): string => percentToNPlaces(p, 2);
const percentTo3Places = (p: number): string => percentToNPlaces(p, 3);

const Results = ({
  program,
  err,
}: {
  program: SandboxResponseProgram;
  err: DecisionCriteriaMismatch | null;
}) => {
  const { loan_details, mortgage_maximums, cash_offer_affordability } = program;

  return (
    <div className={styles.container + (err ? ` ${styles.error}` : "")}>
      <h2>Loan details</h2>
      <table>
        <tbody>
          <tr>
            <td>Loan type</td>
            <td>{humanizeLoanTypeName[loan_details.loan_type] || loan_details.loan_type}</td>
          </tr>
          <tr>
            <td>Decision criteria</td>
            <td>
              {DecisionCriteriaDisplayValues[loan_details.decision_criteria] ||
                loan_details.decision_criteria}
            </td>
          </tr>
          <tr>
            <td>Rate</td>
            <td>{percentTo3Places(loan_details.rate)}</td>
          </tr>
          <tr>
            <td>Qualification rate</td>
            <td>{percentTo3Places(loan_details.qualification_rate)}</td>
          </tr>
          <tr>
            <td>APR</td>
            <td>{percentTo3Places(loan_details.apr)}</td>
          </tr>
        </tbody>
      </table>

      <h2>Mortgage maximums</h2>
      <table>
        <tbody>
          <tr>
            <td>Max purchase price</td>
            <td>{dollarsTo2Places(mortgage_maximums.max_purchase_price)}</td>
          </tr>
          <tr>
            <td>Max loan amount</td>
            <td>{dollarsTo2Places(mortgage_maximums.max_loan_amount)}</td>
          </tr>
          <tr>
            <td>Max downpayment</td>
            <td>{dollarsTo2Places(mortgage_maximums.max_downpayment)}</td>
          </tr>
          <tr>
            <td>LTV at max purchase price & loan amount</td>
            <td>{percentTo2Places(100 * mortgage_maximums.ltv_at_max)}</td>
          </tr>
          <tr>
            <td>Qualifying monthly PITIA at max</td>
            <td>{dollarsTo2Places(mortgage_maximums.pitia_at_max)}</td>
          </tr>
          <tr>
            <td>DTI at max</td>
            <td>{percentTo2Places(100 * mortgage_maximums.dti_at_max)}</td>
          </tr>
          <tr>
            <td>Max allowable DTI</td>
            <td>{percentTo2Places(100 * mortgage_maximums.max_dti)}</td>
          </tr>
          <tr>
            <td>Max eligible LTV at max purchase price</td>
            {mortgage_maximums.eligible_ltv_at_max.override ? (
              <td>
                Override &ndash;{" "}
                {percentTo2Places(100 * mortgage_maximums.eligible_ltv_at_max.override)}
              </td>
            ) : (
              <td>{percentTo2Places(100 * mortgage_maximums.eligible_ltv_at_max.original)}</td>
            )}
          </tr>
          <tr>
            <td>Max LTV</td>
            <td>{percentTo2Places(100 * mortgage_maximums.max_ltv)}</td>
          </tr>
          <tr>
            <td>Calculated required reserves</td>
            <td>{dollarsTo2Places(mortgage_maximums.reserves)}</td>
          </tr>
        </tbody>
      </table>

      <h2>Cash Offer affordability</h2>
      <table>
        <tbody>
          <tr>
            <td>Max Cash Offer purchase price</td>
            <td>{dollarsTo2Places(cash_offer_affordability.max_purchase_price)}</td>
          </tr>
          <tr>
            <td>Max Cash Offer loan amount</td>
            <td>{dollarsTo2Places(cash_offer_affordability.max_loan_amount)}</td>
          </tr>
          <tr>
            <td>Max LTV bucket</td>
            <td>{percentTo2Places(100 * cash_offer_affordability.max_ltv)}</td>
          </tr>
          <tr>
            <td>Min appraisal for max Cash Offer loan amount</td>
            <td>
              {dollarsTo2Places(cash_offer_affordability.min_appraisal_for_cash_offer_loan_amount)}
            </td>
          </tr>
          <tr>
            <td>Appraisal wiggle room at max</td>
            <td>{dollarsTo2Places(cash_offer_affordability.appraisal_wiggle_room_at_max)}</td>
          </tr>
          <tr>
            <td>
              Min appraisal for{" "}
              {dollarsTo2Places(
                cash_offer_affordability.appraisal_wiggle_room_percent.purchase_price,
              )}{" "}
              purchase price
            </td>
            <td>{dollarsTo2Places(cash_offer_affordability.min_appraisal_for_purchase_price)}</td>
          </tr>
          <tr>
            <td>Appraisal wiggle room funds</td>
            <td>{dollarsTo2Places(cash_offer_affordability.appraisal_wiggle_room_for_funds)}</td>
          </tr>
          <tr>
            <td>
              Appraisal wiggle room as % of{" "}
              {dollarsTo2Places(
                cash_offer_affordability.appraisal_wiggle_room_percent.purchase_price,
              )}
            </td>
            <td>
              {percentTo2Places(cash_offer_affordability.appraisal_wiggle_room_percent.percentage)}
            </td>
          </tr>
        </tbody>
      </table>
      <div className={styles.rework}>
        {/* rework request form is inline with the results rather than in a modal in case users want to copy values from the results for the rework request */}
        <ReworkForm err={err} />
      </div>
    </div>
  );
};

export default Results;
